import { McError } from '@common/errors/mc-error';
import { PackagedError } from '@common/errors/types/packaged-error.type';
import { instanceOfHttpErrorResponse, instanceOfMadCloudError, instanceOfMadCloudResult, instanceOfWrappedError } from '@common/errors/util/types';
import { ErrorCode } from '@common/http/enums/error-code.enum';
import { array } from '@common/util/array';

/**
 * Extracts a flat list of human-readable messages from any error-like value — an Error/McError, a
 * MadCloud error or result, an HTTP error response, a wrapped error, a string, or an array of any of
 * these — recursing into nested shapes. Empty messages are dropped.
 * @param error The value to extract messages from.
 * @returns The non-empty messages, or undefined when there are none.
 */
export function getErrorMessages(error: unknown): string[] {
  let errorMessages: string[];

  if (!error) {
    return errorMessages;
  }

  if (error instanceof McError) {
    errorMessages = getErrorMessages((error.name ? error.name + ': ' : '') + (error.message ?? '') + (typeof error.code === 'number' ? ` (${error.code})` : ''));
  } else if (error instanceof Error) {
    errorMessages = getErrorMessages((error.name ? error.name + ': ' : '') + (error.message ?? ''));
  } else if (instanceOfWrappedError(error)) {
    errorMessages = getErrorMessages(error.error);
  } else if (instanceOfHttpErrorResponse(error)) {
    errorMessages = getErrorMessages(array(error.error?.ModelState?.errors) ?? error.error?.Message ?? array(error.error?.Errors) ?? error.error?.error ?? error.statusText);
  } else if (instanceOfMadCloudError(error)) {
    errorMessages = getErrorMessages(error.Message);
  } else if (instanceOfMadCloudResult(error)) {
    errorMessages = getErrorMessages(error.Errors);
  } else if (Array.isArray(error)) {
    errorMessages = error.flatMap(err => getErrorMessages(err));
  } else if (typeof error === 'string') {
    const trimmed = error.trim();
    errorMessages = trimmed ? [trimmed] : undefined;
  }

  // Remove empty messages
  if (Array.isArray(errorMessages)) {
    errorMessages = errorMessages.filter(message => !!message);
  }

  // Return undefined if there are no messages
  return errorMessages?.length > 0 ? errorMessages : undefined;
}

/**
 * Extracts the McErrors carried by a packaged error: a 404 HTTP response maps to a single
 * NotFoundError, MadCloud results/errors (and arrays of them) map to their coded McErrors, and an
 * McError passes through. Falls back to a single UnknownError when nothing else matches.
 * @param error The value to extract McErrors from.
 * @returns The McErrors (never empty).
 */
export function getMcErrors(error: PackagedError): McError[] {
  if (instanceOfHttpErrorResponse(error) && error.status === 404) {
    return [new McError('Not found.', ErrorCode.NotFoundError)];
  } else if (instanceOfHttpErrorResponse(error) && instanceOfMadCloudResult(error.error)) {
    if (Array.isArray(error.error.Errors)) {
      return error.error.Errors.filter(madCloudError => Number.isInteger(madCloudError.Code)).map(madCloudError => new McError(madCloudError.Message, madCloudError.Code));
    }
  } else if (instanceOfMadCloudResult(error)) {
    if (Array.isArray(error.Errors)) {
      return error.Errors.filter(madCloudError => Number.isInteger(madCloudError.Code)).map(madCloudError => new McError(madCloudError.Message, madCloudError.Code));
    }
  } else if (error instanceof McError) {
    return [error];
  } else if (Array.isArray(error)) {
    return error.map(err => {
      if (err instanceof McError) {
        return err;
      } else if (instanceOfMadCloudError(err) && Number.isInteger(err.Code)) {
        return new McError(err.Message, err.Code);
      }
    }).filter(err => !!err);
  }

  return [new McError('Unknown error.', ErrorCode.UnknownError)];
}

/**
 * Returns the ErrorCode of each McError extracted from the packaged error (see getMcErrors).
 * @param error The value to extract error codes from.
 * @returns The error codes.
 */
export function getErrorCodes(error: PackagedError): ErrorCode[] {
  return getMcErrors(error).map(err => err.code);
}

/**
 * Determines whether the packaged error carries an McError with the given code.
 * @param error The value to inspect.
 * @param errorCode The code to look for.
 * @returns True when a matching McError is present.
 */
export function hasErrorCode(error: PackagedError, errorCode: ErrorCode): boolean {
  return getMcErrors(error).some(err => err.code === errorCode);
}

/**
 * Returns the message of the first McError in the packaged error that matches the given code.
 * @param error The value to inspect.
 * @param errorCode The code to look for.
 * @returns The matching message, or undefined when none matches.
 */
export function getErrorMessageForCode(error: PackagedError, errorCode: ErrorCode): string {
  return getMcErrors(error).find(err => err.code === errorCode)?.message;
}

/** The status-carrying shapes seen across the codebase, as read by getHttpStatus and hasHttpStatus. */
interface HttpStatusCarrier {
  status?: number;
  error?: { HttpStatusCode?: number };
  response?: { status?: number };
}

/**
 * Extracts the HTTP status code carried by a caught value, checking the shapes seen across the
 * codebase — a top-level `status` (fetch/HttpErrorResponse), a nested `error.HttpStatusCode`
 * (MadCloud), and a nested `response.status` (axios) — and returning the first one present.
 * @param error The value to inspect.
 * @returns The status code, or undefined when none is present.
 */
export function getHttpStatus(error: unknown): number {
  const httpError = error as HttpStatusCarrier;
  return httpError?.status ?? httpError?.error?.HttpStatusCode ?? httpError?.response?.status;
}

/**
 * Determines whether any of the HTTP status fields a caught value carries equals the given code.
 * @param error The value to inspect.
 * @param statusCode The HTTP status code to look for.
 * @returns True when any of the fields matches.
 * @remarks
 * Every field is checked rather than just the first one present (as getHttpStatus returns), because
 * a MadCloud response can carry a semantic status in `error.HttpStatusCode` that differs from the
 * transport `status` — and `status` is non-optional on HttpErrorResponse, so first-one-wins would
 * make the nested field unreachable.
 */
export function hasHttpStatus(error: unknown, statusCode: number): boolean {
  const httpError = error as HttpStatusCarrier;
  return httpError?.status === statusCode
    || httpError?.error?.HttpStatusCode === statusCode
    || httpError?.response?.status === statusCode;
}

/**
 * Coerces any caught value into a real Error. Returns the value unchanged when it already is one
 * (preserving identity for rethrow); otherwise wraps its formatted message — falling back to
 * `String(error)` when there is no name/message (e.g. a thrown string) — in a new Error. Use when a
 * callee/field is typed `Error` and you are holding a `catch` binding of type `unknown`.
 * @param error The caught value to coerce.
 * @returns The value itself when it is an Error, otherwise a new Error describing it.
 */
export function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(errorToString(error) || String(error));
}

/**
 * Safely extracts an error's `name` and `message` from any caught value, for building error
 * responses/results. Missing fields become empty strings.
 * @param error The caught value to read.
 * @returns The error's name and message, each '' when absent or non-string.
 */
export function toErrorInfo(error: unknown): { name: string; message: string } {
  const errorLike = error as { name?: unknown; message?: unknown };
  return {
    name: typeof errorLike?.name === 'string' ? errorLike.name : '',
    message: typeof errorLike?.message === 'string' ? errorLike.message : ''
  };
}

/**
 * Builds the single-entry `{ [errorName]: errorMessage }` record that collab's controllers send as
 * their error-response body, safely extracting name/message from any caught value.
 * @param error The caught value to read.
 * @returns A record keyed by the error's name with its message as the value.
 */
export function toErrorRecord(error: unknown): Record<string, string> {
  const { name, message } = toErrorInfo(error);
  return { [name]: message };
}

/**
 * Formats any caught value as a `name: message` string, omitting whichever part is absent. Only the
 * `name` and `message` properties are read, so it is safe on a `catch` binding of type `unknown`.
 * @param error The value to format.
 * @returns The formatted string, or '' for a falsy value or one with no name/message.
 */
export function errorToString(error: unknown): string {
  if (!error) {
    return '';
  }

  const errorLike = error as { name?: string; message?: string };
  let err = '';

  if (errorLike.name) {
    err += errorLike.name
  }

  if (errorLike.name && errorLike.message) {
    err += ': ';
  }

  if (errorLike.message) {
    err += errorLike.message;
  }

  return err;
}
